var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import styled from 'styled-components';
import InputMask from 'react-input-mask';
import { withForwardedRef } from '../utils/helpers/reactHelpers';
import FormControl from '../FormControl';
import uid from '../utils/uid';
import ButtonIcon from '../Button/ButtonIcon';
import SvgShow from '../Icon/SvgShow';
import SvgHide from '../Icon/SvgHide';
const InputActiveBoxShadow = (p) => `${p.theme.SizeShadowInputActive} ${p.theme.ColorShadowInputActive}`;
const InputErrorActiveBoxShadow = (p) => `${p.theme.SizeShadowInputActive} ${p.theme.ColorShadowInputErrorActive}`;
const InputSCWrapper = styled.div `
  font-size: ${(p) => p.theme.SizeFontInputBase};
  line-height: ${(p) => p.theme.SizeLineHeightInputBase};
  color: ${(p) => p.theme.ColorFontInputBase};

  display: inline-flex;
  align-items: center;
  position: relative;
  flex-direction: row-reverse;

  /* input */
  input {
    height: ${(p) => p.theme.SizeHeightInputBase};
  }
  textarea {
    min-width: 15rem;
  }
  input,
  textarea {
    color: inherit;
    width: 100%;
    outline: 0;
    border-radius: ${(p) => p.theme.SizeBorderRadiusInputBase};
    border: ${(p) => p.theme.SizeBorderWidthDefault} solid
      ${(p) => p.theme.ColorBorderInputBase};
    padding: ${(p) => p.theme.SizePaddingInputBase};
    padding-bottom: calc(
      ${(p) => p.theme.SizePaddingInputBase} + 0.0625rem
    );

    ._ndsFormControl:hover & {
      border-color: ${(p) => p.theme.ColorBorderInputHover};
    }

    &:focus,
    &:active {
      box-shadow: ${InputActiveBoxShadow};
      border-color: ${(p) => p.theme.ColorBorderInputActive};
    }
    will-change: box-shadow;
    transition: box-shadow 0.3s;
  }

  /* input-icon */
  ._ndsInput__icon {
    display: flex;
    align-items: center;
    > button {
      margin: 0;
    }
    margin-left: -2.5rem;
  }

  /* error */
  &._ndsInput--error input,
  &._ndsInput--error textarea {
    ._ndsFormControl:hover &,
    & {
      border-color: ${(p) => p.theme.ColorBorderError};
    }
    &:focus,
    &:active {
      box-shadow: ${InputErrorActiveBoxShadow};
    }
  }

  /* disabled */
  &._ndsInput--disabled input,
  &._ndsInput--disabled textarea {
    user-select: none;

    ._ndsFormControl:hover &,
    & {
      border-color: ${(p) => p.theme.ColorBorderInputDisabled};
    }
    &:focus,
    &:active {
      box-shadow: none;
    }
    color: ${(p) => p.theme.ColorFontInputDisabled};
    background-color: ${(p) => p.theme.ColorBackgroundInputDisabled};

    /* fix for iOS Safari */
    -webkit-text-fill-color: ${(p) => p.theme.ColorFontInputDisabled};
    opacity: 1;
  }

  /* with Icon */
  &._ndsInput--hasIcon {
    left: -0.5rem;
  }
  &._ndsInput--hasIcon input,
  &._ndsInput--hasIcon textarea {
    padding-right: 2.5rem;
  }
  &._ndsInput--hasIcon textarea {
    margin-left: 0.5rem;
  }
`;
const StyledInput = styled.input ``;
const StyledTextarea = styled.textarea ``;
const StyledMaskedInput = styled(InputMask) ``;
const Input = (props) => {
    const { label, as: asProp, helpText, inputIcon, autoFocus, disabled, error, fullWidth, readOnly, required, id, name, type, value: valueProp, className: classNameProp, onChange, inputRef, forwardedRef, isMasked, hidePasswordEye } = props, others = __rest(props, ["label", "as", "helpText", "inputIcon", "autoFocus", "disabled", "error", "fullWidth", "readOnly", "required", "id", "name", "type", "value", "className", "onChange", "inputRef", "forwardedRef", "isMasked", "hidePasswordEye"]);
    const { current: isControlled } = React.useRef(valueProp != null);
    const [valueState, setValueState] = React.useState(valueProp || '');
    const [showPasswordState, setShowPasswordState] = React.useState(false);
    const inputId = React.useMemo(() => id || uid('_ndsInput_'), [id]);
    const helpTextId = `help-text-${inputId}`;
    const isPassword = type === 'password';
    const value = isControlled ? valueProp : valueState;
    const InputComponentToRender = React.useMemo(() => {
        if (asProp === 'textarea') {
            return StyledTextarea;
        }
        return isMasked ? StyledMaskedInput : StyledInput;
    }, [asProp, isMasked]);
    const showPasswordIcon = isPassword && !hidePasswordEye;
    const handleToggleShowPassword = React.useCallback(() => {
        if (disabled) {
            return;
        }
        setShowPasswordState(!showPasswordState);
    }, [disabled, showPasswordState]);
    const handleChange = React.useCallback((event) => {
        if (!isControlled) {
            setValueState(event.target.value);
        }
        if (onChange) {
            onChange(event);
        }
    }, [onChange, isControlled]);
    const renderType = () => {
        if (isPassword && showPasswordState) {
            return 'text';
        }
        return type;
    };
    const hasIcon = React.useMemo(() => inputIcon != null && asProp !== 'textarea', [asProp, inputIcon]);
    const classes = clsx('_ndsInput', { '_ndsInput--error': error === true }, { '_ndsInput--disabled': disabled === true }, { '_ndsInput--fullWidth': fullWidth === true }, {
        '_ndsInput--hasIcon': hasIcon || type === 'password'
    }, classNameProp);
    const otherInputProps = {};
    if (isMasked) {
        others.maskChar = others.maskChar || null;
        otherInputProps.inputRef = inputRef;
    }
    else {
        otherInputProps.ref = inputRef;
    }
    const passwordIcon = React.useMemo(() => (showPasswordState ? React.createElement(SvgShow, null) : React.createElement(SvgHide, null)), [showPasswordState]);
    return (React.createElement(FormControl, { ref: forwardedRef, className: classNameProp, disabled: disabled, error: error, required: required, fullWidth: fullWidth, inputId: inputId, helpTextId: helpTextId, label: label, helpText: helpText },
        React.createElement(InputSCWrapper, { className: classes },
            showPasswordIcon ? (React.createElement("div", { className: "_ndsInput__icon" },
                React.createElement(ButtonIcon, { color: "grey", onClick: handleToggleShowPassword, type: "button" }, passwordIcon))) : (hasIcon && React.createElement("div", { className: "_ndsInput__icon" }, inputIcon)),
            React.createElement(InputComponentToRender, Object.assign({ forwardedAs: asProp, "aria-describedby": helpTextId, autoFocus: autoFocus, disabled: disabled, readOnly: readOnly, required: required, id: inputId, name: name, type: renderType(), value: value, onChange: handleChange }, others, otherInputProps)))));
};
Input.propTypes = {
    label: PropTypes.string.isRequired,
    as: PropTypes.oneOf(['textarea']),
    helpText: PropTypes.any,
    inputIcon: PropTypes.element,
    autoFocus: PropTypes.bool,
    disabled: PropTypes.bool,
    error: PropTypes.bool,
    fullWidth: PropTypes.bool,
    readOnly: PropTypes.bool,
    required: PropTypes.bool,
    onChange: PropTypes.func,
    id: PropTypes.string,
    name: PropTypes.string,
    type: PropTypes.string,
    value: PropTypes.any,
    className: PropTypes.string,
    inputRef: PropTypes.any,
    forwardedRef: PropTypes.any,
    isMasked: PropTypes.bool,
    hidePasswordEye: PropTypes.bool
};
Input.defaultProps = {
    as: null,
    helpText: undefined,
    inputIcon: undefined,
    autoFocus: false,
    disabled: false,
    error: false,
    fullWidth: false,
    readOnly: false,
    required: false,
    id: undefined,
    name: undefined,
    type: 'text',
    value: undefined,
    className: undefined,
    onChange: undefined,
    forwardedRef: null,
    inputRef: null,
    isMasked: false,
    hidePasswordEye: false
};
export { Input as InputRootComp };
export default withForwardedRef(Input);
//# sourceMappingURL=Input.js.map